import {
  nsfwBrowsingLevelsFlag,
  sfwBrowsingLevelsFlag,
} from '~/shared/constants/browsingLevel.constants';
import { MODEL_NAME_MAX_LENGTH } from '~/shared/constants/model-name.constants';
import { Flags } from '~/shared/utils/flags';

export const SFW_NAME_TOO_LONG_MESSAGE = `Title must be ${MODEL_NAME_MAX_LENGTH} characters or fewer.`;
export const SFW_DESCRIPTION_MAX_LENGTH = 50_000;
export const SFW_DESCRIPTION_TOO_LONG_MESSAGE = 'Description is too long.';

/**
 * A model's .com face: `Model.sfwName` / `sfwDescription`, shown in place of the name and
 * description to viewers who see no mature content. Not `sfwOnly`, which restricts what the
 * resource may generate.
 */
export type ModelSfwFaceSource = {
  name: string;
  description?: string | null;
  sfwName?: string | null;
  sfwDescription?: string | null;
};

/** An empty input or an empty RTE document is stored as NULL, never as a blank face. */
export function normalizeSfwFaceText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === '<p></p>') return null;
  return value ?? null;
}

/**
 * True when the viewer's browsing level holds no mature bits: everyone on .com, and anyone signed
 * out. An unknown level means a server-side caller with no viewer, which keeps the .red face.
 */
export function showsSfwFace(browsingLevel: number | null | undefined) {
  return !!browsingLevel && !Flags.intersects(browsingLevel, nsfwBrowsingLevelsFlag);
}

/**
 * The name and description this viewer should see, without the stored .com pair. A face is on
 * when `sfwName` is set; its description is not filled from the .red one when left blank.
 * Returns a new object, as callers pass rows from shared caches.
 */
export function resolveModelFace<T extends ModelSfwFaceSource>(
  model: T,
  showSfwFace: boolean
): Omit<T, 'sfwName' | 'sfwDescription'> {
  const { sfwName, sfwDescription, ...rest } = model;
  if (!showSfwFace || sfwName == null) return rest;
  return {
    ...rest,
    name: sfwName,
    ...('description' in model ? { description: sfwDescription ?? null } : {}),
  };
}

/**
 * Swaps the model name on rows that name a model by id (image resources, version lists,
 * notification details) for its .com title. `sfwNames` holds only models with a face on, and is
 * empty for a viewer who gets the .red face.
 */
export function withSfwModelNames<T extends { modelId: number; modelName: string }>(
  rows: T[],
  sfwNames: ReadonlyMap<number, string>
): T[] {
  if (!sfwNames.size) return rows;
  return rows.map((row) => {
    const sfwName = sfwNames.get(row.modelId);
    return sfwName ? { ...row, modelName: sfwName } : row;
  });
}

/** Normalises a write that carries the face. Clearing the title turns the face off, description included. */
export function normalizeSfwFaceInput(data: {
  sfwName?: string | null;
  sfwDescription?: string | null;
}) {
  if (data.sfwName !== undefined) data.sfwName = normalizeSfwFaceText(data.sfwName);
  if (data.sfwDescription !== undefined)
    data.sfwDescription = normalizeSfwFaceText(data.sfwDescription);
  if (data.sfwName === null) data.sfwDescription = null;
}

/** The name a search hit renders with; only model hits carry a face. */
export function searchHitName(
  hit: { name: string; sfwName?: string | null },
  showSfwFace: boolean
) {
  return (showSfwFace && hit.sfwName) || hit.name;
}

/**
 * Whether a model's .com face is complete: a .com title and a .com cover the level computation
 * accepted, which is what puts safe bits in a mature model's `nsfwLevel`.
 */
export function hasCompleteSfwFace(model: {
  sfwName?: string | null;
  sfwCoverImageId?: number | null;
  nsfwLevel: number;
}) {
  return (
    model.sfwName != null &&
    model.sfwCoverImageId != null &&
    Flags.intersects(model.nsfwLevel, sfwBrowsingLevelsFlag)
  );
}

/** The cover a viewer gets: the .com one with a complete face, otherwise the creator's .red pick. */
export function pickCoverImageId(
  model: { hasSfwFace?: boolean; coverImageId?: number | null; sfwCoverImageId?: number | null },
  showSfwFace: boolean
) {
  return (showSfwFace && model.hasSfwFace ? model.sfwCoverImageId : model.coverImageId) ?? null;
}

/** Puts the chosen cover first. Returns the list unchanged when there is no cover. */
export function hoistCoverImage<T extends { id: number }>(images: T[], cover: T | undefined): T[] {
  if (!cover) return images;
  return [cover, ...images.filter((image) => image.id !== cover.id)];
}

/** A mature model listed on .com through its face sends that viewer only its PG/PG-13 images. */
export function imagesForFace<T extends { nsfwLevel: number }>(
  images: T[],
  model: { nsfw?: boolean; hasSfwFace?: boolean },
  showSfwFace: boolean
) {
  if (!showSfwFace || !model.nsfw || !model.hasSfwFace) return images;
  return images.filter(
    (image) =>
      image.nsfwLevel !== 0 && (image.nsfwLevel & sfwBrowsingLevelsFlag) === image.nsfwLevel
  );
}
